import { useSupabaseClient } from "@supabase/auth-helpers-react";
import React, { createContext, useContext, useState } from "react";
import { formatPhoneNumberToE164 } from "../components/utls/utils";
import { useToast } from "./ToastContext";

interface LoginAuthContextProps {
  isLoading: boolean;
  loginError: string | null;
  login: (data: {
    email?: string;
    phone?: string;
    password?: string;
  }) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

const SigninAuthContext = createContext<LoginAuthContextProps | undefined>(
  undefined
);

export function useLoginAuth() {
  const context = useContext(SigninAuthContext);
  if (context === undefined) {
    throw new Error("useSigninAuth must be used within a SigninAuthProvider");
  }
  return context;
}

export function LoginAuthProvider({ children }: { children: React.ReactNode }) {
  const supabase = useSupabaseClient();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { showToast } = useToast();

  async function login(data: {
    email?: string;
    phone?: string;
    password?: string;
  }) {
    setIsLoading(true);
    setLoginError(null);

    try {
      if (data.phone) {
        const formattedPhone = formatPhoneNumberToE164(data.phone);

        const { error: phoneError } = await supabase.auth.signInWithOtp({
          phone: formattedPhone,
        });

        if (phoneError) {
          setLoginError(phoneError.message);
          showToast(phoneError.message, "error");
        } else {
          window.location.href = `/verify-phone?phone=${formattedPhone}`;
          showToast("Please verify your phone number", "success");
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: data.email as string,
          password: data.password as string,
        });

        if (error) {
          setLoginError(error.message);
          showToast(error.message, "error");
        } else {
          showToast("Signed in successfully", "success");
        }
      }
    } catch (error: any) {
      setLoginError(error.message || "An error occurred during sign in");
      showToast(error.message || "An error occurred during sign in", "error");
    } finally {
      setIsLoading(false);
    }
  }

  async function signInWithGoogle() {
    setIsLoading(true);
    setLoginError(null);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.href,
        },
      });

      if (error) {
        setLoginError(error.message);
        showToast(error.message, "error");
      }
    } catch (error: any) {
      setLoginError(error.message || "An error occurred during Google sign in");
      showToast(
        error.message || "An error occurred during Google sign in",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  }

  const value = {
    isLoading,
    loginError,
    login,
    signInWithGoogle,
  };

  return (
    <SigninAuthContext.Provider value={value}>
      {children}
    </SigninAuthContext.Provider>
  );
}

